import { useTranslation } from 'react-i18next';
import Modal from '@components/atoms/modals/modal';
import { useThemeContext } from '@utils/hooks/use-theme-context';

export type ShortcutGroup = {
  label: string;
  items: { keys: string[]; description: string }[];
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  groups: ShortcutGroup[];
  title?: string;
}

/**
 * Discoverable list of all editor keyboard shortcuts (#1920 a11y).
 *
 * Rendered as a Modal so screen readers and keyboard users get focus trap +
 * ESC-to-close for free. Each key is a real <kbd> element so AT announces it
 * as a keyboard shortcut, and copy-paste preserves the keyboard semantics.
 *
 * Open with the "?" shortcut (Shift + /) or via the toolbar help button.
 */
export default function KeyboardShortcutsModal({
  isOpen,
  onClose,
  groups,
  title
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title={title || t('keyboard-shortcuts')}
      caption={t('keyboard-shortcuts-caption')}
    >
      <div className="space-y-6">
        {groups.map(group => (
          <section
            key={group.label}
            aria-labelledby={`shortcut-group-${group.label}`}
          >
            <h3
              id={`shortcut-group-${group.label}`}
              className="text-sm font-semibold mb-2"
              style={{ color: getColour('normalText') }}
            >
              {group.label}
            </h3>
            <dl
              className="divide-y divide-gray-300"
              style={{ borderColor: getColour('hairline') }}
            >
              {group.items.map(item => (
                <div
                  key={item.description + item.keys.join('+')}
                  className="flex items-start justify-between gap-3 py-2"
                >
                  <dt
                    className="text-sm flex-1 min-w-0"
                    style={{ color: getColour('normalText') }}
                  >
                    {item.description}
                  </dt>
                  <dd className="flex items-center gap-1 flex-shrink-0">
                    {item.keys.map((k, i) => (
                      <span key={i} className="flex items-center gap-1">
                        {i > 0 && (
                          <span
                            aria-hidden="true"
                            className="text-xs opacity-50"
                            style={{ color: getColour('inactiveText') }}
                          >
                            +
                          </span>
                        )}
                        <kbd
                          className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded border text-xs font-mono"
                          style={{
                            backgroundColor: getColour('thirdLayer'),
                            borderColor: getColour('hairline'),
                            color: getColour('normalText')
                          }}
                        >
                          {k}
                        </kbd>
                      </span>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Modal>
  );
}

/* ─── Pre-built shortcut groups for each editor ───────────────────── */

export const transcriptShortcutGroups = (
  t: (k: string) => string
): ShortcutGroup[] => [
  {
    label: t('shortcuts-group-playback'),
    items: [
      { keys: ['Space'], description: t('shortcut-play-pause') },
      { keys: ['Ctrl', 'Space'], description: t('shortcut-play-pause-typing') },
      { keys: ['F5'], description: t('shortcut-play-pause-typing') },
      { keys: ['F7'], description: t('shortcut-rewind-5s') },
      { keys: ['F8'], description: t('shortcut-forward-5s') },
      { keys: ['←'], description: t('shortcut-rewind-5s') },
      { keys: ['→'], description: t('shortcut-forward-5s') }
    ]
  },
  {
    label: t('shortcuts-group-navigation'),
    items: [
      { keys: ['Ctrl', 'F'], description: t('shortcut-focus-search') },
      { keys: ['Ctrl', 'L'], description: t('shortcut-toggle-linked-playback') }
    ]
  },
  {
    label: t('shortcuts-group-document'),
    items: [
      { keys: ['Ctrl', 'Z'], description: t('shortcut-undo') },
      { keys: ['Ctrl', 'Y'], description: t('shortcut-redo') },
      { keys: ['Ctrl', 'S'], description: t('shortcut-save') }
    ]
  },
  {
    label: t('shortcuts-group-help'),
    items: [{ keys: ['Shift', '?'], description: t('shortcut-open-shortcuts') }]
  }
];

export const captionShortcutGroups = (
  t: (k: string) => string
): ShortcutGroup[] => [
  {
    label: t('shortcuts-group-playback'),
    items: [
      { keys: ['Space'], description: t('shortcut-play-pause') },
      { keys: ['F5'], description: t('shortcut-play-pause-typing') },
      {
        keys: ['Ctrl', 'Shift', 'P'],
        description: t('shortcut-replay-segment')
      }
    ]
  },
  {
    label: t('shortcuts-group-segment-timing'),
    items: [
      {
        keys: ['Numpad 4'],
        description: t('shortcut-segment-start-back-50ms')
      },
      {
        keys: ['Numpad 6'],
        description: t('shortcut-segment-end-forward-50ms')
      },
      {
        keys: ['Ctrl', '['],
        description: t('shortcut-segment-start-back-50ms')
      },
      {
        keys: ['Ctrl', ']'],
        description: t('shortcut-segment-end-forward-50ms')
      },
      {
        keys: ['Ctrl', 'Shift', '['],
        description: t('shortcut-segment-fine-start-plus')
      },
      {
        keys: ['Ctrl', 'Shift', ']'],
        description: t('shortcut-segment-fine-start-minus')
      },
      {
        keys: ['Ctrl', 'Alt', '['],
        description: t('shortcut-segment-fine-end-plus')
      },
      {
        keys: ['Ctrl', 'Alt', ']'],
        description: t('shortcut-segment-fine-end-minus')
      }
    ]
  },
  {
    label: t('shortcuts-group-segment-edit'),
    items: [
      { keys: ['Ctrl', 'Enter'], description: t('shortcut-insert-segment') },
      {
        keys: ['Ctrl', 'Shift', 'X'],
        description: t('shortcut-split-segment')
      },
      {
        keys: ['Ctrl', 'Shift', 'Backspace'],
        description: t('shortcut-delete-segment')
      }
    ]
  },
  {
    label: t('shortcuts-group-document'),
    items: [
      { keys: ['Ctrl', 'Z'], description: t('shortcut-undo') },
      { keys: ['Ctrl', 'Y'], description: t('shortcut-redo') },
      { keys: ['Ctrl', 'S'], description: t('shortcut-save') }
    ]
  },
  {
    label: t('shortcuts-group-help'),
    items: [{ keys: ['Shift', '?'], description: t('shortcut-open-shortcuts') }]
  }
];
